"use client";

import React, { useMemo, useState } from "react";
import {
  createUserReactionForSpecial,
  getTotalReactionCountForSpecial,
  getUserReactionCountForSpecial,
} from "@/data/userSpecialsReactions";

import {
  FeedEvents,
  FollowComedianEvents,
  SpecialEvents,
  trackEvent,
} from "@punchup/analytics";

import { CheckoutModalOrDrawer } from "@punchup/ui-common/components/checkout";
import { Comedian } from "@/data/comedians";
import { Database } from "@punchup/data/supabase.types";
import { FOLLOW_LOCATIONS } from "@/data/follows";
import { ReactionButtonWithData } from "./withReactionData";
import { SpecialRow } from "@/data/specials";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { useFeatureGate } from "statsig-react";

interface VideoReactionButtonProps {
  special: SpecialRow;
  comedian: Comedian;
  authenticated: boolean;
  className?: string;
}

export function VideoReactionButton({
  special,
  comedian,
  authenticated,
  className,
}: VideoReactionButtonProps) {
  const supabase = createClientComponentClient<Database>();
  const [modalOpen, setModalOpen] = useState(false);

  // Use video_comments_stableid gate to control animation style
  const { gate } = useFeatureGate("video_comments_stableid");
  const isVideoCommentsEnabled = gate.value === true;

  const config = useMemo(
    () => ({
      fetchInitialData: async () => {
        const [total, user] = await Promise.all([
          getTotalReactionCountForSpecial(supabase, special.id, "clap"),
          authenticated
            ? getUserReactionCountForSpecial(supabase, special.id, "clap")
            : Promise.resolve(0),
        ]);
        return { total, user };
      },

      submitReaction: async (count: number) => {
        await createUserReactionForSpecial(supabase, special.id, "clap", count);
        trackEvent(SpecialEvents.REACTION_SUBMITTED, {
          type: "clap",
          specialId: special.id,
          clapCount: count,
        });
      },

      pollingInterval: special.livestream ? 30_000 : 10 * 60_000,

      analytics: {
        trackClick: (data: any) => {
          trackEvent(SpecialEvents.REACTION_BUTTON_CLICKED, {
            type: "clap",
            specialId: special.id,
            count: data.totalCount,
            addedMaxClaps: data.totalCount >= 20,
          });
        },
        trackSubmit: () => {}, // Already tracked in submitReaction
      },
    }),
    [supabase, special.id, special.livestream, authenticated]
  );

  return (
    <>
      <ReactionButtonWithData
        config={config}
        size="md"
        className={className}
        authenticated={authenticated}
        onAuthRequired={() => {
          trackEvent(FollowComedianEvents.CLAP_REACTION_BUTTON, {
            followLocation: FOLLOW_LOCATIONS.CLAP_REACTION_BUTTON,
            comedianId: comedian.id,
            comedianName: comedian.display_name,
          });
          setModalOpen(true);
        }}
        animationStyle={isVideoCommentsEnabled ? "dynamic" : "simple"}
        enableParticles={isVideoCommentsEnabled}
      />

      <CheckoutModalOrDrawer
        isOpen={modalOpen}
        setIsOpen={setModalOpen}
        comedianToFollow={comedian}
        special={special}
        redirectTo="/"
        followLocation={FOLLOW_LOCATIONS.CLAP_REACTION_BUTTON}
        onOpen={(params) => {
          trackEvent(FeedEvents.OPENED_SUBSCRIPTION_MODAL, params);
        }}
      />
    </>
  );
}
